import { useMemo } from 'react';
import type { FunnelStatus, User, Task } from 'src/api/generated';
import { Avatar } from 'src/modules/shared/components/atoms/avatar';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import type { ContactAddress } from 'src/api/generated';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { PiArrowLeft } from 'react-icons/pi';
import { getConversationHeaderAvatarText } from 'src/modules/communications/utils/conversation-detail.util';
import { ConversationHeaderActionButtons } from './conversation-header-action-buttons';
import { ConversationHeaderMetadataChips } from './conversation-header-metadata-chips';
import { ConversationHeaderTitleButton } from './conversation-header-title-button';

export const ConversationHeaderMetadata = ({
  addresses,
  aiStatusIds,
  contactId,
  conversationName,
  funnelStatusId,
  handleCall,
  handleOpenTaskModal,
  handleStatusChange,
  handleUserAssign,
  isLoading,
  isSmallScreen,
  isTestConversation,
  loadingAgents,
  loadingStatuses,
  onClose,
  onOpenProfile,
  tasks,
  updatingConversation,
  userId,
  users,
  statuses,
}: {
  addresses?: ContactAddress[];
  aiStatusIds: number[];
  contactId?: number;
  conversationName: string;
  funnelStatusId?: number;
  handleCall: (phoneNumber: string) => void;
  handleOpenTaskModal: () => void;
  handleStatusChange: (statusId: number | null) => Promise<void>;
  handleUserAssign: (userId: number | null) => Promise<void>;
  isLoading: boolean;
  isSmallScreen: boolean;
  isTestConversation: boolean;
  loadingAgents: boolean;
  loadingStatuses: boolean;
  onClose: () => void;
  onOpenProfile: (contactId: number) => void;
  tasks: Task[];
  updatingConversation: boolean;
  userId?: number;
  users: User[];
  statuses: FunnelStatus[];
}) => {
  const { t } = useI18n();
  const overflowItems = useMemo(
    () => [
      {
        content: t('communications.conversationHeader.viewContactProfile'),
        onClick: () => onOpenProfile(contactId ?? 0),
        disabled: !contactId,
      },
      {
        content: t(
          'communications.contactPanel.conversations.fields.viewTasks'
        ),
        onClick: handleOpenTaskModal,
      },
    ],
    [contactId, handleOpenTaskModal, onOpenProfile, t]
  );

  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-x-2 gap-y-2 sm:gap-x-3">
      <div className="row-span-2 flex min-h-full items-center">
        <Button
          variant="neutral"
          appearance="ghost"
          size={isSmallScreen ? 'sm' : 'md'}
          isIconOnly
          onClick={onClose}
          aria-label={t(
            'communications.conversationHeader.backToConversations'
          )}
        >
          <PiArrowLeft className="h-5 w-5" />
        </Button>
      </div>

      <div className="flex min-w-0 items-center gap-3 py-0.5">
        <Avatar
          text={getConversationHeaderAvatarText(conversationName)}
          size="md"
          onClick={() => onOpenProfile(contactId ?? 0)}
        />
        <div className="flex min-w-0 items-center gap-2">
          <ConversationHeaderTitleButton
            contactId={contactId}
            conversationName={conversationName}
            onOpenProfile={onOpenProfile}
          />
        </div>
      </div>

      <div className="row-span-2 flex min-h-full items-center justify-self-end gap-2">
        <ConversationHeaderActionButtons
          addresses={addresses}
          conversationName={conversationName}
          handleCall={handleCall}
          handlerSize={isSmallScreen ? 'sm' : 'md'}
          isLoading={isLoading}
          items={overflowItems}
          t={t}
        />
      </div>

      <ConversationHeaderMetadataChips
        aiStatusIds={aiStatusIds}
        funnelStatusId={funnelStatusId}
        handleOpenTaskModal={handleOpenTaskModal}
        handleStatusChange={handleStatusChange}
        handleUserAssign={handleUserAssign}
        isTestConversation={isTestConversation}
        isSmallScreen={isSmallScreen}
        loadingAgents={loadingAgents}
        loadingStatuses={loadingStatuses}
        statuses={statuses}
        tasks={tasks}
        updatingConversation={updatingConversation}
        userId={userId}
        users={users}
      />
    </div>
  );
};
